import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showHeader, setShowHeader] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const lastScrollY = useRef(0);
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const isHome = location.pathname === "/";
  // Transparent only at the top of the home page (over the hero)
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const controlHeader = () => {
      const currentY = window.scrollY;
      setScrolled(currentY > 40);
      // hide on scroll-down, reveal on scroll-up
      setShowHeader(currentY <= lastScrollY.current || currentY < 80);
      lastScrollY.current = currentY;
    };
    window.addEventListener("scroll", controlHeader, { passive: true });
    controlHeader();
    return () => window.removeEventListener("scroll", controlHeader);
  }, []);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setMenuOpen(false);
    setShowLanguageDropdown(false);
  };

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
    setShowLanguageDropdown(false);
  }, [location]);

  const navigationItems = [
    { path: "/gallery", label: t("nav.gallery") },
    { path: "/booking", label: t("nav.booking") },
    { path: "/restauration-full", label: t("nav.restauration") },
    { path: "/overview", label: t("nav.overview") },
    { path: "/about", label: t("nav.about") },
  ];

  const languages = [
    { code: "en", name: "EN" },
    { code: "fr", name: "FR" },
    { code: "de", name: "DE" },
    { code: "ar", name: "ع" },
  ];

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) || languages[0];

  const linkColor = transparent
    ? "text-white/85 hover:text-white"
    : "text-ink-soft hover:text-ink";

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          showHeader ? "translate-y-0" : "-translate-y-full"
        } ${
          transparent
            ? "bg-transparent"
            : "bg-cream/95 backdrop-blur-md border-b border-ink/10 shadow-[0_1px_20px_rgba(28,27,24,0.04)]"
        }`}
      >
        <div className="max-w-8xl mx-auto flex items-center justify-between px-5 md:px-10 h-16 md:h-20">
          {/* Wordmark */}
          <Link to="/" className="group flex flex-col leading-none">
            <span
              className={`font-serif text-2xl md:text-3xl tracking-wide transition-colors duration-500 ${
                transparent ? "text-white" : "text-ink"
              }`}
            >
              Laurier{" "}
              <span className={transparent ? "text-brass-light" : "text-brass"}>
                Rose
              </span>
            </span>
            <span
              className={`eyebrow mt-0.5 text-[10px] tracking-[0.3em] transition-colors duration-500 ${
                transparent ? "text-white/60" : "text-ink-muted"
              }`}
            >
              Ourika Valley
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-9">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative text-[13px] uppercase tracking-[0.18em] font-light transition-colors duration-300 ${linkColor} after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-brass after:transition-all after:duration-300 hover:after:w-full`}
              >
                {item.label}
              </Link>
            ))}
            <a
              href="https://www.google.com/maps/search/?api=1&query=31.300988,-7.724062"
              target="_blank"
              rel="noopener noreferrer"
              className={`relative text-[13px] uppercase tracking-[0.18em] font-light transition-colors duration-300 ${linkColor} after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-brass after:transition-all after:duration-300 hover:after:w-full`}
            >
              {t("nav.viewMap")}
            </a>
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-3 md:gap-5">
            {/* Language switcher (desktop) */}
            <div className="hidden lg:flex items-center gap-1">
              {languages.map((lang, idx) => (
                <React.Fragment key={lang.code}>
                  <button
                    onClick={() => changeLanguage(lang.code)}
                    className={`text-xs tracking-widest transition-colors duration-200 ${
                      i18n.language === lang.code
                        ? transparent
                          ? "text-white"
                          : "text-brass"
                        : transparent
                        ? "text-white/50 hover:text-white"
                        : "text-ink-muted hover:text-ink"
                    }`}
                  >
                    {lang.name}
                  </button>
                  {idx < languages.length - 1 && (
                    <span
                      className={transparent ? "text-white/30" : "text-ink/20"}
                    >
                      /
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Book CTA (desktop) */}
            <Link
              to="/booking"
              className={`hidden md:inline-flex items-center text-[12px] uppercase tracking-[0.2em] px-6 py-3 transition-all duration-300 ${
                transparent
                  ? "border border-white/70 text-white hover:bg-white hover:text-ink"
                  : "bg-ink text-cream hover:bg-brass"
              }`}
            >
              {t("nav.bookNow")}
            </Link>

            {/* Mobile language */}
            <div className="relative lg:hidden">
              <button
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                className={`flex items-center gap-1 text-xs tracking-widest ${
                  transparent ? "text-white/80" : "text-ink-soft"
                }`}
              >
                <span>{currentLanguage.name}</span>
                <motion.span
                  animate={{ rotate: showLanguageDropdown ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </motion.span>
              </button>
              <AnimatePresence>
                {showLanguageDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowLanguageDropdown(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full right-0 mt-3 bg-cream border border-ink/10 shadow-lg z-50 min-w-[70px] py-1"
                    >
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => changeLanguage(lang.code)}
                          className={`w-full text-left px-4 py-2 text-xs tracking-widest transition-colors ${
                            i18n.language === lang.code
                              ? "text-brass"
                              : "text-ink-soft hover:bg-cream-dark"
                          }`}
                        >
                          {lang.name}
                        </button>
                      ))}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Menu"
              className="lg:hidden flex flex-col items-center justify-center w-7 h-7 relative"
            >
              <span
                className={`absolute w-6 h-px transition-all duration-300 ${
                  transparent ? "bg-white" : "bg-ink"
                } ${menuOpen ? "rotate-45 top-3.5" : "top-2"}`}
              />
              <span
                className={`absolute w-6 h-px transition-all duration-300 ${
                  transparent ? "bg-white" : "bg-ink"
                } ${menuOpen ? "opacity-0" : "top-3.5"}`}
              />
              <span
                className={`absolute w-6 h-px transition-all duration-300 ${
                  transparent ? "bg-white" : "bg-ink"
                } ${menuOpen ? "-rotate-45 top-3.5" : "top-5"}`}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile overlay menu */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-ink/50 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setMenuOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 32, stiffness: 320 }}
              className="fixed top-0 right-0 bottom-0 w-full max-w-sm bg-cream z-50 lg:hidden flex flex-col"
            >
              <div className="flex items-center justify-between px-6 h-20 border-b border-ink/10">
                <span className="font-serif text-2xl text-ink">
                  Laurier <span className="text-brass">Rose</span>
                </span>
                <button
                  onClick={() => setMenuOpen(false)}
                  aria-label="Close menu"
                  className="w-9 h-9 flex items-center justify-center text-ink-muted hover:text-ink transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto">
                <nav className="px-6 py-8">
                  <ul className="space-y-1">
                    {navigationItems.map((item, index) => (
                      <motion.li
                        key={item.path}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.07 }}
                      >
                        <Link
                          to={item.path}
                          onClick={() => setMenuOpen(false)}
                          className="block py-4 font-serif text-2xl text-ink hover:text-brass transition-colors border-b border-ink/5"
                        >
                          {item.label}
                        </Link>
                      </motion.li>
                    ))}
                    <motion.li
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: navigationItems.length * 0.07 }}
                    >
                      <a
                        href="https://www.google.com/maps/search/?api=1&query=31.300988,-7.724062"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setMenuOpen(false)}
                        className="block py-4 font-serif text-2xl text-ink hover:text-brass transition-colors border-b border-ink/5"
                      >
                        {t("nav.viewMap")}
                      </a>
                    </motion.li>
                  </ul>

                  <Link
                    to="/booking"
                    onClick={() => setMenuOpen(false)}
                    className="mt-8 block w-full bg-ink text-cream text-center text-[12px] uppercase tracking-[0.2em] py-4 hover:bg-brass transition-colors"
                  >
                    {t("nav.bookNow")}
                  </Link>
                </nav>

                <div className="px-6 py-6 border-t border-ink/10">
                  <h3 className="eyebrow text-ink-muted mb-4">Language</h3>
                  <div className="flex gap-2">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`px-4 py-2 text-xs tracking-widest border transition-all duration-200 ${
                          i18n.language === lang.code
                            ? "bg-ink text-cream border-ink"
                            : "text-ink-soft border-ink/15 hover:border-brass"
                        }`}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="px-6 py-6 border-t border-ink/10 bg-cream-dark/50">
                  <h3 className="eyebrow text-ink-muted mb-4">Contact</h3>
                  <div className="space-y-2">
                    <p className="text-sm text-ink-soft">Ourika Valley, Morocco</p>
                    <a
                      href="tel:+212661919669"
                      className="block text-sm text-ink hover:text-brass transition-colors"
                    >
                      +212 661-919669
                    </a>
                    <a
                      href="mailto:laurierroseourika@gmail.com"
                      className="block text-sm text-ink hover:text-brass transition-colors"
                    >
                      laurierroseourika@gmail.com
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
